import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import Grid2 from '@mui/material/Grid2';
import { useTheme } from "@mui/material/styles";
import BasePaper from "components/Base/BasePaper";


export interface GenericStaticSummaryProps<
  T extends { id?: number | string }
> {
  record: T | null;
  titleField: keyof T | (keyof T)[];
  descriptionField?: keyof T;
  loading?: boolean;
  rightContent?: React.ReactNode;
  belowContent?: React.ReactNode;

  /** Optional X button */
  onClose?: () => void;
}

export default function GenericStaticSummary<
  T extends { id?: number | string }
>({
  record,
  titleField,
  descriptionField,
  loading = false,
  rightContent,
  belowContent,
  onClose,
}: GenericStaticSummaryProps<T>) {
  const theme = useTheme();

  if (!record) return <Typography>Loading record...</Typography>;

  const renderStaticText = (
    field: keyof T,
    variant: "h3" | "body1" = "body1"
  ) => {
    const value = record[field];
    return (
      <Typography variant={variant} color="text.primary">
        {value as any}
      </Typography>
    );
  };

  const renderTitle = () => {
    if (Array.isArray(titleField)) {
      return (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {titleField.map((field) => (
            <React.Fragment key={String(field)}>
              {renderStaticText(field, "h3")}
            </React.Fragment>
          ))}
        </Box>
      );
    }
    return renderStaticText(titleField, "h3");
  };

  return (
    <BasePaper onClose={onClose}>
      <Grid2 container>
        {/* LEFT SIDE */}
        <Grid2 size={rightContent ? 9 : 12}>
          {renderTitle()}

          {descriptionField && (
            <Box sx={{ mt: 1 }}>{renderStaticText(descriptionField, "body1")}</Box>
          )}

          {belowContent && <Box sx={{ mt: 3 }}>{belowContent}</Box>}
        </Grid2>

        {/* RIGHT SIDE */}
        {rightContent && <Grid2 size={3}>{rightContent}</Grid2>}
      </Grid2>

      {loading && (
        <Box sx={{ mt: 2 }}>
          <CircularProgress size={18} />
        </Box>
      )}
    </BasePaper>
  );

}
